import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { BottomSheet, BottomSheetSwitcher } from '@astryxdesign/core/BottomSheet';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Divider } from '@astryxdesign/core/Divider';
import { Heading } from '@astryxdesign/core/Heading';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Item } from '@astryxdesign/core/Item';
import { List } from '@astryxdesign/core/List';
import { HStack, VStack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { TextArea } from '@astryxdesign/core/TextArea';
import { TextInput } from '@astryxdesign/core/TextInput';
import { useToast } from '@astryxdesign/core/Toast';
import { ArrowLeft, CheckCircle, MapPin, X } from '@phosphor-icons/react';
import { useBookingNav, useStudio, type BookStep } from '../../app/studio';
import { SlotPicker } from '../../components/SlotPicker';
import { ReminderControls } from '../../components/ReminderControls';
import { ApiError, api, humanError, newKey } from '../../lib/api';
import { saveBooking, saveContact, savedContact } from '../../lib/local';
import { bookingFormSchema } from '../../shared/business';
import { dayLabel, durationLabel, fmt, money } from '../../lib/time';
import type { ClientBooking } from '../../lib/types';

/**
 * Booking as one short sequence of bottom sheets:
 * service -> date & time -> contacts -> confirmation.
 * Steps are in the URL, so Back returns to the previous step.
 */
export function BookingFlow() {
  const studio = useStudio();
  const nav = useBookingNav();
  const service = studio.services.find((s) => s.id === nav.serviceId) ?? null;
  // a broken deep link (unknown service) falls back to the service list
  const step: BookStep | null = nav.step && nav.step !== 'service' && !service && nav.step !== 'done' ? 'service' : nav.step;

  return (
    <BottomSheetSwitcher activeSheet={step} onActiveSheetChange={(s) => !s && nav.close()}>
      <BottomSheet sheetId="service" label="Выбор услуги" height="tall">
        <ServiceStep />
      </BottomSheet>
      <BottomSheet sheetId="time" label="Выбор даты и времени" height="tall">
        {service && <TimeStep />}
      </BottomSheet>
      <BottomSheet sheetId="contacts" label="Контакты" height="tall" purpose="form">
        {service && nav.at && <ContactsStep />}
      </BottomSheet>
      <BottomSheet sheetId="done" label="Запись подтверждена" height="tall">
        <DoneStep />
      </BottomSheet>
    </BottomSheetSwitcher>
  );
}

function SheetHeader({ title, onBack }: { title: string; onBack?: () => void }) {
  const nav = useBookingNav();
  return (
    <HStack gap={2} vAlign="center" hAlign="between">
      <HStack gap={2} vAlign="center">
        {onBack && <IconButton label="Назад" variant="ghost" icon={<ArrowLeft size={20} />} onClick={onBack} />}
        <Heading level={2}>{title}</Heading>
      </HStack>
      <IconButton label="Закрыть" variant="ghost" icon={<X size={20} />} onClick={nav.close} />
    </HStack>
  );
}

function ServiceStep() {
  const studio = useStudio();
  const nav = useBookingNav();
  return (
    <VStack gap={3} padding={4}>
      <SheetHeader title="Запись в студию" />
      <Text color="secondary">Выберите услугу</Text>
      <List hasDividers density="spacious">
        {studio.services.map((s) => (
          <Item
            key={s.id}
            label={<Text weight="semibold">{s.name}</Text>}
            description={durationLabel(s.duration_min)}
            endContent={
              <Text weight="semibold" hasTabularNumbers>
                {money(s.price, studio.currency, s.price_from)}
              </Text>
            }
            onClick={() => nav.go('time', { service: s.id, at: null, day: null })}
          />
        ))}
      </List>
    </VStack>
  );
}

function TimeStep() {
  const studio = useStudio();
  const nav = useBookingNav();
  const service = studio.services.find((s) => s.id === nav.serviceId)!;
  const [at, setAt] = useState<string | null>(nav.at);
  const [day, setDay] = useState<string | null>(nav.day);
  const end = at ? new Date(new Date(at).getTime() + service.duration_min * 60000) : null;
  return (
    <VStack gap={4} padding={4}>
      <SheetHeader title="Дата и время" onBack={() => nav.go('service', { at: null, day: null })} />
      <Card padding={4} variant="muted">
        <HStack hAlign="between" vAlign="center" gap={3}>
          <VStack gap={0.5}>
            <Text weight="semibold">{service.name}</Text>
            <Text type="supporting">{durationLabel(service.duration_min)}</Text>
          </VStack>
          <Text weight="semibold" hasTabularNumbers>
            {money(service.price, studio.currency, service.price_from)}
          </Text>
        </HStack>
      </Card>
      <SlotPicker
        slug={studio.slug}
        serviceId={service.id}
        timezone={studio.timezone}
        value={at}
        initialDay={day}
        onChange={(iso, d) => {
          setAt(iso);
          setDay(d);
        }}
      />
      {service.duration_min >= 1440 && end && (
        <Text type="supporting">
          Машина останется в студии до {fmt(end, studio.timezone, 'd MMMM, HH:mm')}.
        </Text>
      )}
      <Button
        label={at ? `Далее · ${dayLabel(day!, studio.timezone).toLowerCase()}, ${fmt(at, studio.timezone, 'HH:mm')}` : 'Выберите время'}
        variant="primary"
        size="lg"
        width="100%"
        isDisabled={!at}
        onClick={() => nav.go('contacts', { at: at!, day: day! })}
      />
    </VStack>
  );
}

type Field = 'name' | 'phone' | 'car' | 'comment';
// TextInput forwards extra props to <input>; its types just do not list inputMode
const TEL = { inputMode: 'tel' } as Record<string, string>;

function ContactsStep() {
  const studio = useStudio();
  const nav = useBookingNav();
  const toast = useToast();
  const qc = useQueryClient();
  const service = studio.services.find((s) => s.id === nav.serviceId)!;
  const at = nav.at!;
  const [form, setForm] = useState(() => ({ ...savedContact(), comment: '' }));
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [busy, setBusy] = useState(false);
  // one key per intent (service + time); reused on retry or double tap
  const keyRef = useRef<{ for: string; key: string } | null>(null);
  const intent = `${service.id}|${at}`;
  if (!keyRef.current || keyRef.current.for !== intent) keyRef.current = { for: intent, key: newKey() };

  const set = (f: Field) => (v: string) => {
    setForm((x) => ({ ...x, [f]: v }));
    if (errors[f]) setErrors((e) => ({ ...e, [f]: undefined }));
  };

  const submit = async () => {
    if (busy) return;
    const parsed = bookingFormSchema.safeParse(form);
    if (!parsed.success) {
      const errs: Partial<Record<Field, string>> = {};
      for (const i of parsed.error.issues) errs[i.path[0] as Field] ??= i.message;
      setErrors(errs);
      return;
    }
    setBusy(true);
    try {
      const b = await api.createBooking({
        slug: studio.slug, serviceId: service.id, startsAt: at, name: parsed.data.name, phone: parsed.data.phone,
        car: parsed.data.car, comment: parsed.data.comment, key: keyRef.current!.key,
      });
      saveContact({ name: parsed.data.name, phone: form.phone, car: parsed.data.car });
      saveBooking(studio.slug, { token: b.token!, id: b.id, startsAt: b.starts_at, serviceName: b.service_name });
      qc.setQueryData(['my-booking', b.token], b);
      void qc.invalidateQueries({ queryKey: ['slots', studio.slug] });
      nav.go('done', { bid: b.id, at: null, day: null }, true);
    } catch (e) {
      if (e instanceof ApiError && (e.code === 'slot_taken' || e.code === 'slot_in_past' || e.code === 'slot_outside_hours')) {
        toast({ body: e.message, type: 'error' });
        await qc.invalidateQueries({ queryKey: ['slots', studio.slug, service.id] });
        nav.go('time', { at: null });
      } else {
        toast({ body: humanError(e), type: 'error' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <VStack gap={4} padding={4}>
        <SheetHeader title="Ваши контакты" onBack={() => nav.go('time')} />
        <Card padding={4} variant="muted">
          <VStack gap={1}>
            <Text weight="semibold">{service.name}</Text>
            <Text>
              {dayLabel(nav.day!, studio.timezone)}, {fmt(at, studio.timezone, 'd MMMM')} в {fmt(at, studio.timezone, 'HH:mm')}
            </Text>
            <Text type="supporting">
              {durationLabel(service.duration_min)} · {money(service.price, studio.currency, service.price_from)}
            </Text>
          </VStack>
        </Card>
        <TextInput
          label="Имя"
          value={form.name}
          onChange={set('name')}
          autoComplete="name"
          isRequired
          status={errors.name ? { type: 'error', message: errors.name } : undefined}
        />
        <TextInput
          label="Телефон"
          value={form.phone}
          onChange={set('phone')}
          autoComplete="tel"
          {...TEL}
          placeholder="+7 900 123-45-67"
          description="Студия позвонит, только если что-то изменится"
          isRequired
          status={errors.phone ? { type: 'error', message: errors.phone } : undefined}
        />
        <TextInput
          label="Автомобиль"
          value={form.car}
          onChange={set('car')}
          placeholder="Марка и модель"
          isRequired
          status={errors.car ? { type: 'error', message: errors.car } : undefined}
        />
        <TextArea label="Комментарий" value={form.comment} onChange={set('comment')} rows={2} isOptional maxLength={500} />
        <Button type="submit" label="Записаться" variant="primary" size="lg" width="100%" isLoading={busy} />
        <Text type="supporting" justify="center">
          {studio.cancel_policy_text ?? `Отменить запись онлайн можно не позднее чем за ${studio.cancel_policy_hours} ч.`}
        </Text>
      </VStack>
    </form>
  );
}

function DoneStep() {
  const studio = useStudio();
  const nav = useBookingNav();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const saved = useMemo(() => {
    try {
      return (JSON.parse(localStorage.getItem(`studio:${studio.slug}:bookings`) ?? '[]') as { id: string; token: string }[]).find(
        (b) => b.id === nav.bookingId,
      );
    } catch {
      return undefined;
    }
  }, [studio.slug, nav.bookingId]);
  const [booking, setBooking] = useState<ClientBooking | undefined>(() =>
    saved ? qc.getQueryData<ClientBooking>(['my-booking', saved.token]) : undefined,
  );
  useEffect(() => {
    if (!booking && saved) void api.myBooking(saved.token).then((b) => b && setBooking(b));
  }, [booking, saved]);

  if (!booking || !saved) {
    return (
      <VStack gap={4} padding={4}>
        <SheetHeader title="Запись" />
        <Text color="secondary">Открываем запись…</Text>
      </VStack>
    );
  }
  return (
    <VStack gap={5} padding={4}>
      <HStack hAlign="end">
        <IconButton label="Закрыть" variant="ghost" icon={<X size={20} />} onClick={nav.close} />
      </HStack>
      <VStack gap={2} hAlign="center">
        <CheckCircle size={56} weight="fill" color="var(--color-accent)" aria-hidden />
        <Heading level={2} justify="center">
          Вы записаны
        </Heading>
        <Text color="secondary" justify="center">
          {booking.is_demo ? 'Это демонстрационная запись в образце приложения.' : 'Студия уже видит вашу запись.'}
        </Text>
      </VStack>
      <Card padding={4}>
        <VStack gap={2}>
          <Text weight="semibold" type="large">
            {fmt(booking.starts_at, studio.timezone, 'EEEE, d MMMM')} в {fmt(booking.starts_at, studio.timezone, 'HH:mm')}
          </Text>
          <Text>{booking.service_name}</Text>
          <Text type="supporting">
            {booking.car} · {money(booking.price, booking.currency, booking.price_from)}
          </Text>
          <Divider />
          <HStack gap={2} vAlign="start">
            <MapPin size={18} aria-hidden />
            <Text type="supporting">{studio.address}</Text>
          </HStack>
        </VStack>
      </Card>
      <ReminderControls booking={booking} token={saved.token} />
      <Button
        label="Открыть мою запись"
        variant="primary"
        size="lg"
        width="100%"
        onClick={() => {
          nav.close();
          navigate(`/s/${studio.slug}/my`);
        }}
      />
    </VStack>
  );
}
